import React from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import LegalFooter from './shared/LegalFooter';
import { 
  Shield,
  Lock,
  Eye,
  FileText,
  UserCheck,
  Clock,
  AlertTriangle,
  Mail,
  CheckCircle,
} from 'lucide-react';

const PrivacyPage = () => {
  const sections = [
    {
      id: "purpose",
      title: "1. Purpose and Scope",
      icon: FileText,
      content: "This policy applies to all personal and non-personal data collected by Smart AI Solutions Tech Ltd in connection with our services, focusing on SMART Voice and AI Automation Systems for HR and hospitality industries. It governs how we handle data collected from businesses primarily in the UK and EU, with occasional operations outside these regions."
    },
    {
      id: "collection",
      title: "2. Data Collection",
      icon: Eye,
      content: "We collect the following types of information:",
      list: [
        "Personal Data: Name, email address, and payment information.",
        "Non-Personal Data: Usage patterns, analytics data, and device information."
      ]
    },
    {
      id: "purpose-legal-basis",
      title: "3. Purpose and Legal Basis of Data Collection",
      icon: UserCheck,
      content: "We collect and process data for the following purposes:",
      list: [
        "To improve our services and optimise performance.",
        "To personalise user experiences.",
        "For analytics to better understand usage patterns.",
        "For marketing purposes, including service updates and promotions."
      ],
      additionalContent: "The legal bases for data processing under GDPR include:",
      additionalList: [
        "User consent",
        "Legitimate business interests", 
        "Performance of a contract",
        "Legal obligations"
      ]
    },
    {
      id: "storage",
      title: "4. Data Storage",
      icon: Lock,
      content: "All data is stored securely using cloud services with robust encryption protocols. Data is primarily stored with Supabase, hosted within European data centres to maintain GDPR compliance. Data may be processed locally or internationally, depending on operational requirements."
    },
    {
      id: "sharing",
      title: "5. Data Sharing",
      icon: Shield,
      content: "We do not share user data with third parties, except:",
      list: [
        "As required by law",
        "With explicit user consent",
        "With trusted subprocessors who are bound by data protection agreements"
      ]
    },
    {
      id: "retention",
      title: "6. Data Retention",
      icon: Clock,
      content: "Data is retained for 12 months following the end of user activity or contract termination unless otherwise required by law or necessary for legitimate business purposes. At the end of the retention period, data will be securely deleted or anonymised."
    },
    {
      id: "security",
      title: "7. Security Measures",
      icon: Shield,
      content: "We implement strict safeguards to protect data, including:",
      list: [
        "Encryption of data at rest and in transit",
        "Use of firewalls and secure authentication mechanisms",
        "Role-based access controls and audit logging",
        "Regular security audits and vulnerability monitoring"
      ]
    },
    {
      id: "breach",
      title: "8. Breach Notification Procedures",
      icon: AlertTriangle,
      content: "In the event of a data breach, we will:",
      list: [
        "Notify affected users within 72 hours, as required by GDPR",
        "Provide details of the breach, including the nature of the data exposed, potential risks, and mitigation steps",
        "Report the breach to the relevant data protection authority"
      ]
    },
    {
      id: "rights",
      title: "9. User Rights",
      icon: UserCheck,
      content: "Under GDPR, users have the following rights:",
      list: [
        "Access: Request a copy of the personal data we hold",
        "Rectification: Request corrections to inaccurate or incomplete data",
        "Erasure: Request deletion of personal data (\"Right to be Forgotten\")",
        "Restriction: Request to restrict the processing of their data",
        "Data Portability: Request transfer of their data to another service provider",
        "Objection: Object to data processing under certain circumstances"
      ],
      footer: "To exercise these rights, users can contact us at info@smartaisolutions.ai."
    },
    {
      id: "cookies",
      title: "10. Cookies and Tracking",
      icon: Eye,
      content: "We use cookies and similar tracking technologies to:",
      list: [
        "Enhance website functionality and improve user experience",
        "Collect analytical data to optimise our services"
      ],
      footer: "Users can manage cookie preferences through their browser settings or via the cookie consent banner on our website."
    },
    {
      id: "ai-voice",
      title: "11. AI and Voice Data Processing",
      icon: Shield,
      content: "As part of our Smart AI Solutions services (including VioQbase, VioReceptionist, VioConcierge, and VioCoach), we use AI-powered voice processing. Voice data is processed in real-time to generate answers and is not stored unless explicitly permitted by the client. Transcripts, summaries, or insights are retained only where necessary to fulfil support, compliance, or service improvement requirements. All such data is anonymised and subject to strict access controls."
    },
    {
      id: "compliance",
      title: "12. Compliance",
      icon: CheckCircle,
      content: "We comply with the General Data Protection Regulation (GDPR) and adhere to its principles of transparency, accountability, and user rights. We also monitor emerging standards such as the EU AI Act and NIST AI RMF."
    },
    {
      id: "updates",
      title: "13. Policy Updates",
      icon: FileText,
      content: "This policy is reviewed and updated periodically to reflect changes in our practices or legal requirements. Updates will be posted on our website, and users will be notified of significant changes via email or a prominent site notification."
    },
    {
      id: "contact",
      title: "14. Contact Information",
      icon: Mail,
      content: "For questions about this policy or to exercise your data rights, please contact:",
      footer: "Email: info@smartaisolutions.ai"
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                <Shield className="h-10 w-10 text-[#ff7a1f]" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Data Security & <span className="text-[#ff7a1f]">Privacy Policy</span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed mb-8">
              At Smart AI Solutions Tech Ltd, we are committed to protecting your data and ensuring its security and privacy.
            </p>
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <Clock className="h-5 w-5 text-[#ff7a1f]" />
              <span className="text-blue-200">Effective Date: 21st January 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl bg-white">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center justify-center space-x-3">
                  <FileText className="h-6 w-6 text-[#ff7a1f]" />
                  <span>Policy Overview</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 text-lg leading-relaxed">
                  This policy outlines how we collect, use, store, and safeguard your information while complying with applicable data protection laws, including the General Data Protection Regulation (GDPR).
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto space-y-8">
            {sections.map((section, index) => (
              <Card key={section.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-bold text-[#112d56] flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-lg flex items-center justify-center">
                      <section.icon className="h-5 w-5 text-[#ff7a1f]" />
                    </div>
                    <span>{section.title}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    {section.content}
                  </p>
                  
                  {section.list && (
                    <ul className="space-y-2 ml-4">
                      {section.list.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-[#ff7a1f] rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.additionalContent && (
                    <>
                      <p className="text-gray-700 leading-relaxed mt-4">
                        {section.additionalContent}
                      </p>
                      {section.additionalList && (
                        <ul className="space-y-2 ml-4">
                          {section.additionalList.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-[#ff7a1f] rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                  
                  {section.footer && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-700 font-medium">
                        {section.footer}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
              Questions About Your <span className="text-[#ff7a1f]">Data Rights</span>?
            </h2>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
              We're here to help you understand how your data is protected and to assist with any privacy-related inquiries.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-8">
              <Button 
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-12 py-6 text-xl rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
                onClick={() => window.location.href = 'mailto:info@smartaisolutions.ai'}
              >
                <Mail className="mr-3 h-6 w-6" />
                Contact Us About Privacy
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default PrivacyPage;